import { DateTime } from "luxon";

/** @param {unknown} value */
const asRecord = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : null;

/** @param {unknown} value */
export const extractRelationId = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "number" || typeof value === "string") return value;

  const record = asRecord(value);
  if (!record) return null;

  if ("value" in record) {
    const inner = record.value;
    if (typeof inner === "number" || typeof inner === "string") return inner;
    const innerRecord = asRecord(inner);
    if (innerRecord?.id != null) return innerRecord.id;
  }

  if (record.id != null) return record.id;
  return null;
};

/** @param {unknown} value @param {'en'|'fr'} locale */
const localizedText = (value, locale) => {
  if (typeof value === "string") return value.trim();
  const record = asRecord(value);
  if (!record) return "";
  const picked = record[locale] ?? record.en ?? record.fr;
  return typeof picked === "string" ? picked.trim() : "";
};

/**
 * @param {string|null|undefined} publishAt
 * @param {string|null|undefined} unpublishAt
 * @param {Date} [now]
 */
export const isNavbarPublished = (publishAt, unpublishAt, now = new Date()) => {
  if (!publishAt && !unpublishAt) return true;
  if (!publishAt) return false;

  const zoneNow = DateTime.fromJSDate(now).setZone("America/New_York");

  const publish = DateTime.fromISO(publishAt, { zone: "America/New_York" }).set({
    hour: DateTime.fromISO(publishAt).hour || 5,
    minute: DateTime.fromISO(publishAt).minute || 0,
    second: 0,
    millisecond: 0,
  });
  if (!publish.isValid) return false;

  if (unpublishAt) {
    const unpublish = DateTime.fromISO(unpublishAt, { zone: "America/New_York" }).set({
      hour: DateTime.fromISO(unpublishAt).hour || 5,
      minute: DateTime.fromISO(unpublishAt).minute || 0,
      second: 0,
      millisecond: 0,
    });
    if (!unpublish.isValid) return false;
    return zoneNow >= publish && zoneNow < unpublish;
  }

  return zoneNow >= publish;
};

/** @param {unknown} image */
export const resolveNavbarImageUrl = (image) => {
  const media = asRecord(image);
  if (!media) return undefined;

  const folder =
    (typeof media.folder === "string" && media.folder.trim()) ||
    (typeof media.directory === "string" && media.directory.trim()) ||
    (typeof media.prefix === "string" && media.prefix.trim()) ||
    "";
  const filename = typeof media.filename === "string" ? media.filename.trim() : "";
  if (folder && filename) {
    return `${folder.replace(/^\/+|\/+$/g, "")}/${filename}`;
  }

  if (typeof media.url === "string" && media.url.trim()) {
    try {
      const { pathname } = new URL(media.url);
      return pathname
        .replace(/^\/+/, "")
        .replace(/^pgfe\/img\//i, "")
        .replace(/^fit-in\/[^/]+\//i, "");
    } catch {
      return media.url.replace(/^\/+/, "");
    }
  }

  return filename || undefined;
};

/** @param {Object} doc @param {'en'|'fr'} locale */
export const resolveNavbarItemRoute = (doc, locale) => {
  const linkType = doc.linkType ?? "custom-url";

  if (linkType === "custom-url") {
    const url = typeof doc.customUrl === "string" ? doc.customUrl.trim() : "";
    return url || undefined;
  }

  const linked = doc.linkedContent;
  const linkedRecord = asRecord(linked);
  if (!linkedRecord) return undefined;

  const relationTo =
    typeof linkedRecord.relationTo === "string"
      ? linkedRecord.relationTo
      : asRecord(linkedRecord.relationTo)?.slug;

  const value = linkedRecord.value ?? linked;
  const related = asRecord(value) ?? linkedRecord;

  if (relationTo === "promotions" || related.url != null) {
    const url = typeof related.url === "string" ? related.url.trim() : "";
    return url || undefined;
  }

  if (relationTo === "high-hand-promotions" || related.slug != null) {
    const slug = typeof related.slug === "string" ? related.slug.trim() : "";
    if (!slug) return undefined;
    const prefix = locale === "fr" ? "/fr" : "";
    return `${prefix}/promotions/poker/${slug}`;
  }

  return undefined;
};

/** @param {Object} a @param {Object} b */
export const compareNavbarSiblingOrder = (a, b) => {
  const byOrder = String(a._order ?? "").localeCompare(String(b._order ?? ""));
  if (byOrder !== 0) return byOrder;
  return String(a.id).localeCompare(String(b.id));
};

/** @param {Object} doc @param {'en'|'fr'} locale */
const mapLinkNode = (doc, locale) => {
  const node = {
    id: doc.id,
    title: localizedText(doc.label, locale),
  };

  const route = resolveNavbarItemRoute(doc, locale);
  if (route) node.route = route;
  if (doc.isCategoryTitle) node.categoryTitle = true;
  if (doc.isExternal) node.isExternal = true;
  if (doc.icon && doc.icon !== "none") node.icon = doc.icon;
  if (doc.badge && doc.badge !== "none") node.badge = doc.badge;

  const img = resolveNavbarImageUrl(doc.image);
  if (img) node.img = img;

  const imgAlt = localizedText(doc.imageAlt, locale);
  if (imgAlt) node.imgAlt = imgAlt;

  return node;
};

/** @param {Object} doc @param {Object[]} items @param {'en'|'fr'} locale */
const mapColumnNode = (doc, items, locale) => {
  const node = {
    id: doc.id,
    title: localizedText(doc.label, locale),
    items,
  };

  const mobileTitle = localizedText(doc.mobileLabel, locale);
  if (mobileTitle) node.mobileTitle = mobileTitle;

  const img = resolveNavbarImageUrl(doc.image);
  if (img) node.img = img;

  const imgAlt = localizedText(doc.imageAlt, locale);
  if (imgAlt) node.imgAlt = imgAlt;

  if (doc.navLevel === "column") return node;

  const route = resolveNavbarItemRoute(doc, locale);
  if (route) {
    node.route = route;
    if (doc.isExternal) node.isExternal = true;
  }

  return node;
};

/**
 * @param {Object[]} docs
 * @param {'en'|'fr'} locale
 * @param {Date} [now]
 * @param {{ previewId?: string|number|null }} [options]
 */
export const buildNavbarTree = (docs, locale, now = new Date(), options = {}) => {
  const previewId = options.previewId != null ? String(options.previewId) : null;

  const byParent = new Map();
  for (const doc of docs) {
    if (doc?.id == null || !doc.navLevel) continue;
    const parentId = extractRelationId(doc.parent);
    const key = parentId == null ? null : String(parentId);
    const list = byParent.get(key) ?? [];
    list.push(doc);
    byParent.set(key, list);
  }
  for (const [, list] of byParent) {
    list.sort(compareNavbarSiblingOrder);
  }

  const shouldInclude = (doc) => {
    if (previewId && String(doc.id) === previewId) return true;
    return isNavbarPublished(doc.publishAt, doc.unpublishAt, now);
  };

  const build = (parentId) => {
    const siblings = byParent.get(parentId) ?? [];
    return siblings.flatMap((doc) => {
      if (!shouldInclude(doc)) return [];

      const childNodes = build(String(doc.id));

      if (doc.navLevel === "link") {
        return [mapLinkNode(doc, locale)];
      }

      if (doc.navLevel === "column") {
        if (childNodes.length === 0) return [];
        return [mapColumnNode(doc, childNodes, locale)];
      }

      if (childNodes.length > 0) {
        return [mapColumnNode(doc, childNodes, locale)];
      }

      const route = resolveNavbarItemRoute(doc, locale);
      if (!route) return [];
      return [mapLinkNode(doc, locale)];
    });
  };

  return build(null);
};

/**
 * @param {Object[]} docs
 * @param {Object|null|undefined} liveDoc
 */
export const mergeNavbarLiveDoc = (docs, liveDoc) => {
  if (!liveDoc || liveDoc.id == null) return docs;

  const id = String(liveDoc.id);
  const index = docs.findIndex((doc) => String(doc.id) === id);
  if (index >= 0) {
    const next = [...docs];
    next[index] = { ...docs[index], ...liveDoc };
    return next;
  }
  return [...docs, liveDoc];
};
